import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import WorkflowGraph from './WorkflowGraph';
import WorkflowDAG from './WorkflowDAG';
import CloseIcon from '@mui/icons-material/Close';
import RightPanel from './RightPanel';
import { useTranslation } from 'react-i18next';
import { Autocomplete, IconButton, TextField } from '@totalsoft/rocket-ui';
import { WorkflowGraphDrawer, WorkflowGraphDrawerContent, WorkflowGraphDrawerHeader, WorkflowGraphDrawerMain } from './WorkflowStyles';

const WorkflowGraphContainer = ({ flow, workflows, hasRightToEditWorkflow, actions }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentFlow, setCurrentFlow] = useState(flow);
  const dag = useMemo(() => (currentFlow ? new WorkflowDAG(currentFlow) : null), [currentFlow]);
  const { t } = useTranslation();

  const handleClose = () => {
    setSelectedTask(null);
  };

  useEffect(() => {
    if (flow.workflowId === currentFlow.workflowId) {
      setCurrentFlow(flow);
    } else {
      const _flow = workflows.find(a => a.workflowId === currentFlow.workflowId);
      if (_flow) {
        setCurrentFlow(_flow);
      }
    }
  }, [flow, currentFlow.workflowId, workflows]);

  const handleChangeFlow = _flowId => {
    const _flow = workflows.find(a => a.workflowId === _flowId);
    if (_flow) {
      setSelectedTask(null);
      setCurrentFlow(_flow);
    }
  };

  const handleSelectedTask = task => {
    if (task) {
      setSelectedTask(task);
    } else {
      setSelectedTask(null);
    }
  };

  const getTaskResult = () => {
    let taskResult,
      retryOptions = null;
    if (!selectedTask) {
      return null;
    } else {
      const { ref, taskId } = selectedTask;
      const node = dag.graph.node(ref);
      if (!node) return { taskResult: null, retryOptions: null };
      if (node.taskResults.length > 1) {
        retryOptions = node.taskResults;
      }

      if (taskId) {
        taskResult = node.taskResults.find(task => task.taskId === taskId);
      } else {
        taskResult = _.last(node.taskResults);
      }
    }

    return { taskResult, retryOptions };
  };

  let dfOptions = selectedTask ? dag.dfChildInfo(selectedTask.ref) : null;
  if (selectedTask) {
    var { taskResult, retryOptions } = getTaskResult(selectedTask.ref);
  }
  return (
    <>
      <WorkflowGraph
        selectedTask={selectedTask}
        hasRightToEditWorkflow={hasRightToEditWorkflow}
        executionMode={true}
        dag={dag}
        onClick={setSelectedTask}
        changeFlow={handleChangeFlow}
        actions={actions}
        t={t}
      />
      {selectedTask && (
        <WorkflowGraphDrawer>
          <div id='dragger' />
          <WorkflowGraphDrawerMain>
            <WorkflowGraphDrawerHeader>
              {dfOptions && dfOptions.length > 1 && (
                <div>
                  <Autocomplete
                    renderInput={params => <TextField {...params} label='Task' variant='outlined' />}
                    onChange={v => {
                      setSelectedTask({ ref: v.ref });
                    }}
                    disableClearable
                    options={dfOptions}
                    value={dfOptions.find(opt => opt.ref === selectedTask.ref)}
                    getOptionLabel={x => `${dropdownIcon(x.status)} ${x.ref}`}
                    style={{ width: 400 }}
                  />
                </div>
              )}
              {retryOptions && (
                <div>
                  <Autocomplete
                    renderInput={params => <TextField {...params} label='Retried Task - Select an instance' variant='outlined' />}
                    onChange={v => {
                      setSelectedTask({
                        ref: v.referenceTaskName,
                        taskId: v.taskId
                      });
                    }}
                    disableClearable
                    options={retryOptions}
                    value={retryOptions.find(opt => opt.taskId === taskResult.taskId)}
                    getOptionLabel={t => `${dropdownIcon(t.status)} Attempt ${t.retryCount} - ${t.taskId}`}
                    style={{ width: 400 }}
                  />
                </div>
              )}
              <IconButton tooltip={t('Workflow.Sidebar.Close')} onClick={() => handleClose()}>
                <CloseIcon />
              </IconButton>
            </WorkflowGraphDrawerHeader>
            <WorkflowGraphDrawerContent>
              <RightPanel selectedTask={selectedTask} onTaskChange={handleSelectedTask} onChangeFlow={handleChangeFlow} dag={dag} />
            </WorkflowGraphDrawerContent>
          </WorkflowGraphDrawerMain>
        </WorkflowGraphDrawer>
      )}
    </>
  );
};

function dropdownIcon(status) {
  let icon;
  switch (status) {
    case 'COMPLETED':
      icon = '\u2705';
      break; // Green-checkmark
    case 'COMPLETED_WITH_ERRORS':
      icon = '\u2757';
      break; // Exclamation
    case 'CANCELED':
      icon = '\uD83D\uDED1';
      break; // stopsign
    case 'IN_PROGRESS':
    case 'SCHUEDULED':
      icon = '\u231B';
      break; // hourglass
    default:
      icon = '\u274C'; // red-X
  }
  return icon + '\u2003';
}

WorkflowGraphContainer.propTypes = {
  flow: PropTypes.object.isRequired,
  workflows: PropTypes.array.isRequired,
  hasRightToEditWorkflow: PropTypes.bool,
  actions: PropTypes.object
};

export default WorkflowGraphContainer;
